class ShopifyProduct {
  _ = {};

  constructor(data) {
    this._ = data || {};
  }

  get available() {
    return this._.available;
  }

  get compareAtPrice() {
    return this._.compare_at_price;
  }

  get compareAtPriceMax() {
    return this._.compare_at_price_max;
  }

  get compareAtPriceMin() {
    return this._.compare_at_price_min;
  }

  get compareAtPriceVaries() {
    return this._.compare_at_price_varies;
  }

  get comingSoon() {
    return this._.coming_soon;
  }

  get content() {
    return this._.content;
  }

  get createdAt() {
    return this._.created_at;
  }

  get description() {
    return this._.description;
  }

  get featuredImage() {
    return this._.featured_image;
  }

  get handle() {
    return this._.handle;
  }

  get id() {
    return this._.id;
  }

  get images() {
    return this._.images;
  }

  get media() {
    return this._.media;
  }

  get options() {
    return this._.options;
  }

  get productOptions() {
    return this._.productOptions;
  }

  get price() {
    return this._.price;
  }

  get priceCents() {
    return this._.price_cents;
  }

  get priceFormatted() {
    return this._.price_formatted;
  }

  get originalPrice() {
    return this._.original_price;
  }

  get priceMax() {
    return this._.price_max;
  }

  get priceMin() {
    return this._.price_min;
  }

  get priceVaries() {
    return this._.price_varies;
  }

  get publishedAt() {
    return this._.published_at;
  }

  get requiresSellingPlan() {
    return this._.requires_selling_plan;
  }

  get sellingPlanGroups() {
    return this._.selling_plan_groups;
  }

  get tags() {
    return this._.tags;
  }

  get title() {
    return this._.title;
  }

  get type() {
    return this._.type;
  }

  get variants() {
    return Object.values(this._.variants || {}).filter(Boolean);
  }

  getVariantsForOption(option, value) {
    var optionIndex = this.options.indexOf(option);
    return this.variants.filter((variant) => variant.options[optionIndex] === value);
  }

  getVariantsForOptions(options) {
    var variants = this.variants;

    for (var i = 0; i < options.length; i++) {
      var option = options[i];
      var value = option.value;
      var optionIndex = this.options.indexOf(option.name);
      variants = variants.filter((variant) => variant.options[optionIndex] === value);
    }

    return variants;
  }

  get vendor() {
    return this._.vendor;
  }

  get finishes() {
    return Object.values(this._.finishes || {});
  }

  get dimensions() {
    return Object.values(this._.dimensions || {});
  }

  getSeriesVariants(seriesName) {
    return this.variants.filter((variant) => {
      return variant.series && variant.series.name === seriesName;
    });
  }

  getFinishesForSeries(seriesName) {
    const seriesVariants = this.getSeriesVariants(seriesName);
    const finishes = new Set();
    seriesVariants.forEach((variant) => {
      if (variant.finish && variant.finish.name) {
        finishes.add(variant.finish.name);
      }
    });
    return Array.from(finishes).map((name) => {
      return this.finishes.find((finish) => finish.name === name) || { name: name, color: '', texture: '' };
    });
  }

  getVariantById(id) {
    return this.variants.find((variant) => variant.id == id);
  }

  getVariantBySeriesSizeAndFinish(seriesName, size, finish) {
    const variant = this.variants.find((variant) => {
      return variant.series?.name === seriesName && variant.options.includes(size) && variant.finish?.name === finish;
    });

    return variant;
  }

  getFirstVariantForSeriesAndSize(seriesName, size) {
    const variant = this.variants.find((variant) => {
      return variant.series?.name === seriesName && variant.options.includes(size);
    });

    return variant;
  }

  getVariantByOptions(options) {
    if (!options?.length) {
      return this.variants[0];
    }

    const variant = this.variants.find((variant) => {
      return variant.options.every((option, index) => option === options[index]);
    });

    return variant;
  }

  getClosestMatchingVariant(options) {
    const matches = [];

    for (const variant of this.variants) {
      const matchCount = [...variant.options].reduce((count, option, index) => {
        return count + (option === options[index] ? 1 : 0);
      }, 0);
      matches.push({ variant, matchCount });
    }

    matches.sort((a, b) => b.matchCount - a.matchCount);

    return matches[0]?.variant || null;
  }
}

class ShopifyProductVariant {
  _ = {};

  constructor(data) {
    this._ = data || {};
  }

  get available() {
    return this._.available;
  }

  get barcode() {
    return this._.barcode;
  }

  get compareAtPrice() {
    return this._.compare_at_price;
  }

  get featuredImage() {
    return this._.featured_image;
  }

  get id() {
    return this._.id;
  }

  get inventoryManagement() {
    return this._.inventory_management;
  }

  get inventoryPolicy() {
    return this._.inventory_policy;
  }

  get inventoryQuantity() {
    return this._.inventory_quantity;
  }

  get name() {
    return this._.name;
  }

  get options() {
    return this._.options;
  }

  get price() {
    return this._.price;
  }

  get priceCents() {
    return this._.price_cents;
  }

  get priceFormatted() {
    return this._.price_formatted;
  }

  get originalPrice() {
    return this._.original_price;
  }

  get publicTitle() {
    return this._.public_title;
  }

  get quantityRule() {
    return this._.quantity_rule;
  }

  get requiresSellingPlan() {
    return this._.requires_selling_plan;
  }

  get requiresShipping() {
    return this._.requires_shipping;
  }

  get sellingPlanAllocations() {
    return this._.selling_plan_allocations;
  }

  get sku() {
    return this._.sku;
  }

  get taxable() {
    return this._.taxable;
  }

  get title() {
    return this._.title;
  }

  get weight() {
    return this._.weight;
  }

  get metafields() {
    return this._.metafields;
  }

  get media() {
    return this._.media || [];
  }

  get dimensions() {
    return this._.dimensions || {};
  }

  get finish() {
    return this._.finish || {};
  }

  get series() {
    return this._.series || {};
  }
}

class ShopifyCollection {
  constructor(data) {
    this._ = data || {};
  }

  get id() {
    return this._.id;
  }

  get title() {
    return this._.title;
  }

  get description() {
    return this._.description;
  }

  get products() {
    return Object.values(this._.products || {});
  }

  getProductById(id) {
    return this._.products[id];
  }

  getProductByVariantId(id) {
    return Object.values(this._.products || {}).find((product) => product.variants[id]);
  }

  getProductByOptions(options) {
    return Object.values(this._.products || {}).find((product) => {
      const variants = Object.values(product.variants || {});
      return variants.find((variant) => {
        return variant.options.every((option, index) => option === options[index]);
      });
    });
  }

  getProductVariantByOptions(options) {
    return Object.values(this._.products || {}).find((product) => {
      const variants = Object.values(product.variants || {});
      return variants.find((variant) => variant.options.every((option, index) => option === options[index]));
    });
  }
}

window.ShopifyProduct = ShopifyProduct;
window.ShopifyProductVariant = ShopifyProductVariant;
window.ShopifyCollection = ShopifyCollection;
